import Image from 'next/image'
import { sanitizeHTML } from "@/lib/utils";
import Link from 'next/link';

interface ReadOurTipsInterfaceData {
  published?: boolean;
  heading?: {
    text?: string;
    title?: string;
  };
  button?: {
    href?: string;
    label?: string;
  };
  content_collection?: {
    id: string;
    site_content_collections_id: {
      collection: {
        item: {
          title: string;
          text: string;
          href: string;
          label: string;
        };
      }[];
    };
  }[];
}

export const fields = [
  'published',
  // headings
  "heading.text",
  "heading.title",

  //button
  "button.href",
  "button.label",
  
  // content collection / blog posts
  "content_collection.id",
  "content_collection.site_content_collections_id.collection.item:site_content.title",
  "content_collection.site_content_collections_id.collection.item:site_content.text",
  "content_collection.site_content_collections_id.collection.item:menu_items.href",
  "content_collection.site_content_collections_id.collection.item:menu_items.label",
] as const;

interface ReadOurTipsProps {
  data?: ReadOurTipsInterfaceData;
}

export default function ReadOurTips({ data }: ReadOurTipsProps) {
  if (!data?.published) return null;

  return (
    <section className="w-full bg-[#94a4bc] overflow-hidden">
      <div className="container mx-auto px-8 xl:px-16 py-8 xl:py-32">
        {/* Desktop and Mobile Header */}
        <div className="max-w-4xl mx-auto text-center mb-12 lg:mb-20">
          <h2 className="text-[2rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-white mb-9">
            {data?.heading?.title}
          </h2>
          {data?.heading?.text?.trim() &&
            (data.heading.text.includes("<") ? (
              <div
                className="text-[1rem] font-normal leading-normal lg:text-[1.125rem] text-white font-greycliff tracking-tight [&>*]:text-[1rem] [&>*]:font-normal [&>*]:leading-normal lg:[&>*]:text-[1.125rem]"
                dangerouslySetInnerHTML={sanitizeHTML(data.heading.text)}
              />
            ) : (
              <p className="text-[1rem] font-normal leading-normal lg:text-[1.125rem] text-white font-greycliff tracking-tight">
                {data.heading.text}
              </p>
            ))}
        </div>

        {/* Cards Container */}
        <div className="flex flex-col lg:flex-row gap-5 lg:gap-10">
          {data?.content_collection?.map((collection, index) => {
            const titleItem = collection?.site_content_collections_id?.collection.find(item => item.item.title)?.item;
            const textItem = collection?.site_content_collections_id?.collection.find(item => item.item.text)?.item;
            const menuItem = collection?.site_content_collections_id?.collection.find(item => item.item.href && item.item.label)?.item;

            return (
              <div key={collection.id || index} className="flex-1 p-8 rounded-[1.25rem] border border-white transition-all duration-300 hover:bg-white group">
                <div className="flex flex-col h-full gap-4">
                  <h3 className="text-3xl xl:text-4xl text-white font-essonnes font-light leading-[1.36] group-hover:text-primary">
                    {titleItem?.title}
                  </h3>
                  <p className="hidden lg:block text-white font-greycliff text-xl leading-7 tracking-tight group-hover:text-primary" dangerouslySetInnerHTML={sanitizeHTML(textItem?.text || "")} />
                  { menuItem && (
                    <div className="hidden lg:block mt-auto">
                      <Link href={menuItem?.href || "#"} className="inline-block px-6 py-3.5 border border-white rounded-full text-white text-xl font-medium font-greycliff group-hover:text-primary group-hover:border-primary">
                      {menuItem?.label}
                      </Link>
                    </div>
                  )}
                  { menuItem && (
                    <div className="lg:hidden mt-6">
                      <div className="w-16 h-16 rounded-full border border-white flex items-center justify-center group-hover:border-primary">
                        <Image 
                        src="/images/arrow-right-white.svg"
                        alt="Arrow right"
                        width={24}
                        height={24}
                        quality={90}
                      />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
      
        </div>
        {/* View All Articles Button */}
        {data?.button?.href && data?.button?.label && (
          <div className="w-full mt-10 lg:mt-16 text-center">
            <Link 
              href={data.button.href || "#"} 
              className="rounded-[3.125rem] bg-primary px-9 py-4 font-greycliff text-xl text-white transition-all duration-300 hover:bg-light hover:text-primary"
            >
              {data.button.label}
            </Link>
          </div>
        )}
      </div>
    </section>
  )
} 